import React, { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import { LEGAL_CONTENT } from '../data/legalContent';
import { useLanguage } from '../contexts/LanguageContext';

/**
 * Generic legal page renderer.
 * Props:
 *   pageKey: 'terms' | 'privacy' | 'refund-policy' | 'disclaimer'
 *   lang:    'en' | 'el'
 *
 * Forces the site language to match the page language so the rest of the
 * shell (navbar, footer) stays consistent with the legal text.
 */
const LegalPage = ({ pageKey, lang }) => {
  const navigate = useNavigate();
  const { language, setLanguage } = useLanguage();
  const page = LEGAL_CONTENT?.[lang]?.[pageKey];

  // Keep the global language in sync with the page's language.
  useEffect(() => {
    if (language !== lang && typeof setLanguage === 'function') {
      setLanguage(lang);
    }
  }, [lang, language, setLanguage]);

  // Reset scroll on route entry.
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'instant' });
  }, [pageKey, lang]);

  if (!page) {
    return (
      <div className="min-h-screen bg-[#050811] text-white flex items-center justify-center">
        <p>Page not found.</p>
      </div>
    );
  }

  const updatedLabel = lang === 'el' ? 'Τελευταία ενημέρωση' : 'Last updated';
  const backLabel = lang === 'el' ? 'Επιστροφή στην αρχική' : 'Back to home';

  return (
    <div className="min-h-screen bg-[#050811] text-white">
      <Navbar />
      <main className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 pt-28 pb-16" data-testid={`legal-page-${pageKey}-${lang}`}>
        <button
          onClick={() => navigate('/')}
          className="text-cyan-400 hover:text-cyan-300 text-sm mb-6 inline-flex items-center transition-colors"
          data-testid="legal-back-home"
        >
          ← {backLabel}
        </button>

        <h1 className="text-4xl md:text-5xl font-bold mb-2" data-testid="legal-title">
          {page.title}
        </h1>
        <p className="text-white/50 text-sm mb-10" data-testid="legal-updated">
          {updatedLabel}: {page.lastUpdated}
        </p>

        <article className="space-y-8">
          {page.sections.map((section, idx) => {
            const sectionKey = section.heading || `section-${idx}`;
            return (
              <section key={sectionKey} className="space-y-3">
                {section.heading && (
                  <h2 className="text-xl md:text-2xl font-semibold text-white">{section.heading}</h2>
                )}
                {section.body && section.body.map((para) => (
                  <p key={para.slice(0, 40)} className="text-white/80 leading-relaxed">
                    {para}
                  </p>
                ))}
                {section.bullets && section.bullets.length > 0 && (
                  <ul className="list-disc pl-6 space-y-1.5 text-white/80">
                    {section.bullets.map((b) => (
                      <li key={b.slice(0, 40)}>{b}</li>
                    ))}
                  </ul>
                )}
              </section>
            );
          })}
        </article>
      </main>
      <Footer />
    </div>
  );
};

export default LegalPage;
